import { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import {
  formatRelativeTimeCompact,
  getTranslatedGenre,
  getTranslatedProjectStatus,
  normalizeProjectStatusValue,
} from '../../utils/i18nHelpers';
import ProjectActionsMenu from './ProjectActionsMenu';
import PdfImportModal from './PdfImportModal';
import './ProjectHome.css';

function useStages() {
  const { t } = useTranslation();
  return [
    { id: 'editor', title: t('projects.stages.writingStudio'), icon: '📝', description: t('projects.stages.writingStudioDesc'), status: 'ready', cta: t('projects.stages.openEditor'), path: '/editor' },
    { id: 'accessible', title: t('projects.stages.accessibleStudio', 'Accessible Studio'), icon: '🎤', description: t('projects.stages.accessibleStudioDesc', 'Voice-first mentor guidance with a live transcript and large controls.'), status: 'ready', cta: t('projects.stages.openAccessibleStudio', 'Open Accessible Studio'), path: '/accessible' },
    { id: 'draft-review', title: t('projects.stages.draftReview'), icon: '🔍', description: t('projects.stages.draftReviewDesc'), status: 'ready', cta: t('projects.stages.runDraftReview'), path: '/draft-review' },
    { id: 'reader-preview', title: t('projects.stages.readerPreview'), icon: '📖', description: t('projects.stages.readerPreviewDesc'), status: 'ready', cta: t('projects.stages.openReaderPreview'), path: '/reader-preview' },
    { id: 'marketing', title: t('projects.stages.marketingStudio'), icon: '🎨', description: t('projects.stages.marketingStudioDesc'), status: 'ready', cta: t('projects.stages.openMarketingStudio'), path: '/marketing' },
    { id: 'storefront', title: t('projects.stages.storefrontStudio', 'Storefront Studio'), icon: '🛒', description: t('projects.stages.storefrontStudioDesc', 'Generate a public book page readers can visit and share.'), status: 'ready', cta: t('projects.stages.openStorefrontStudio', 'Open Storefront'), path: '/storefront' },
    { id: 'audiobook', title: t('projects.stages.audiobookStudio'), icon: '🎧', description: t('projects.stages.audiobookStudioDesc'), status: 'coming', cta: t('projects.stages.openAudiobookStudio'), path: '/audiobook' },
    { id: 'translation', title: t('projects.stages.translationStudio'), icon: '🌐', description: t('projects.stages.translationStudioDesc'), status: 'ready', cta: t('projects.stages.openTranslationStudio'), path: '/translation' },
    { id: 'audience-test', title: t('projects.stages.audienceTest'), icon: '📊', description: t('projects.stages.audienceTestDesc'), status: 'ready', cta: t('projects.stages.runAudienceTest'), path: '/audience-test' },
    { id: 'canvas', title: t('projects.stages.canvasStudio'), icon: '🗺️', description: t('projects.stages.canvasStudioDesc'), status: 'ready', cta: t('projects.stages.openCanvasStudio'), path: '/canvas' },
  ];
}

function useBookStages() {
  const { t } = useTranslation();
  return [
    { value: 'drafting', label: t('projects.bookStages.drafting') },
    { value: 'revising', label: t('projects.bookStages.revising') },
    { value: 'editing', label: t('projects.bookStages.editing') },
    { value: 'betaReading', label: t('projects.bookStages.betaReading') },
    { value: 'finalReview', label: t('projects.bookStages.finalReview') },
    { value: 'published', label: t('projects.bookStages.published') },
  ];
}

function ProjectHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const STAGES = useStages();
  const BOOK_STAGES = useBookStages();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  // Core query — returns null gracefully on auth/not-found
  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');

  // Only load dependent queries once we know the project exists
  const projectLoaded = project != null;

  const hasStoryBible = useQuery(
    api.storyBibles.hasCurrent,
    projectLoaded && safeProjectId ? { projectId: safeProjectId } : 'skip',
  );
  const files = useQuery(
    api.files.listByProject,
    projectLoaded && safeProjectId ? { projectId: safeProjectId } : 'skip',
  );
  const updateMetadata = useMutation(api.projects.updateMetadata);

  const [stageOpen, setStageOpen] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const stageRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!stageOpen) return;
    const handler = (e) => {
      if (stageRef.current && !stageRef.current.contains(e.target)) {
        setStageOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [stageOpen]);

  // Compute stats from files
  const stats = useMemo(() => {
    if (!files) return { words: 0, chapters: 0 };
    const chapterFiles = files.filter(
      (f) => f.type === 'file' && f.parentPath === 'Chapters',
    );
    const allContent = files
      .filter((f) => f.type === 'file' && f.content)
      .map((f) => f.content)
      .join(' ');
    const wordCount = allContent.trim() ? allContent.trim().split(/\s+/).length : 0;
    return { words: wordCount, chapters: chapterFiles.length };
  }, [files]);

  const currentStage = normalizeProjectStatusValue(project?.status || 'drafting');

  const handleStageChange = async (newStage) => {
    setStageOpen(false);
    if (newStage !== currentStage && safeProjectId) {
      await updateMetadata({ projectId: safeProjectId, status: newStage });
    }
  };

  if (!safeProjectId) {
    return (
      <div className="project-home loading">
        <div className="project-home-card">
          {t('common.invalidProjectLink')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button>
        </div>
      </div>
    );
  }

  // Loading state
  if (project === undefined) {
    return (
      <div className="project-home loading">
        <div className="project-home-card">{t('projects.loadingProject')}</div>
      </div>
    );
  }

  // Not found state
  if (!project) {
    return (
      <div className="project-home loading">
        <div className="project-home-card">{t('common.projectNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="project-home">
      {/* Top Bar */}
      <div className="ph-topbar">
        <button className="ph-back" onClick={() => navigate('/projects')}>
          {i18n.dir() === 'rtl' ? '→' : '←'} {t('projects.allProjects')}
        </button>
        <div className="ph-topbar-right">
          {hasStoryBible && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/projects/${safeProjectId}/story-bible`)}
            >
              {t('projects.viewStoryBible')}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => setShowPdfImport(true)}
            title={t('pdfImport.uploadPrompt')}
          >
            {t('pdfImport.title')}
          </button>
<button
            className="btn btn-primary"
            onClick={() => navigate(`/projects/${safeProjectId}/editor`)}
          >
            {t('projects.continueWriting')}
          </button>
          <ProjectActionsMenu
            projectId={safeProjectId}
            projectTitle={project.title}
            isPinned={project.pinned}
            onActionError={(msg) => console.error(msg)}
            onDeleteSuccess={() => navigate('/projects')}
          />
        </div>
      </div>

      {/* Hero Card */}
      <div className="ph-hero-card">
        <div className="ph-pills-row">
          {project.genre && (
            <span className="ph-pill ph-pill-genre">{getTranslatedGenre(t, project.genre)}</span>
          )}
          <div className="ph-stage-wrapper" ref={stageRef}>
            <button
              className={`ph-pill ph-pill-stage ${stageOpen ? 'open' : ''}`}
              onClick={() => setStageOpen(!stageOpen)}
            >
              <span className="ph-stage-dot" />
              {getTranslatedProjectStatus(t, currentStage)}
              <span className="ph-stage-chevron">{stageOpen ? '▲' : '▼'}</span>
            </button>
            {stageOpen && (
              <div className="ph-stage-dropdown">
                {BOOK_STAGES.map((stage) => (
                  <button
                    key={stage.value}
                    className={`ph-stage-option ${stage.value === currentStage ? 'active' : ''}`}
                    onClick={() => handleStageChange(stage.value)}
                  >
                    {stage.value === currentStage && <span className="ph-option-dot" />}
                    {stage.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <h1>{project.title}</h1>
        <p className="ph-synopsis">
          {project.synopsis || t('projects.buildStory')}
        </p>
        <div className="ph-hero-stats">
          <div className="ph-stat">
            <div className="ph-stat-value">
              {stats.words.toLocaleString()}
            </div>
            <div className="ph-stat-label">{t('projects.wordsLabel')}</div>
          </div>
          <div className="ph-stat">
            <div className="ph-stat-value">{stats.chapters}</div>
            <div className="ph-stat-label">{t('projects.chaptersLabel')}</div>
          </div>
          <div className="ph-stat">
            <div className="ph-stat-value">
              {formatRelativeTimeCompact(t, project.updatedAt) || '—'}
            </div>
            <div className="ph-stat-label">{t('projects.lastEdit')}</div>
          </div>
        </div>
      </div>

      {/* Tools & Studios */}
      <div className="ph-section-label">{t('projects.toolsAndStudios')}</div>
      <div className="ph-tools-grid">
        {STAGES.map((stage) => {
          const isReady = stage.status === 'ready';
          return (
            <div
              key={stage.id}
              className={`ph-tool ${!isReady ? 'locked' : ''}`}
              onClick={() =>
                isReady && navigate(`/projects/${safeProjectId}${stage.path}`)
              }
            >
              <span className="ph-tool-icon">{stage.icon}</span>
              <div className="ph-tool-body">
                <h3>
                  {stage.title}
                  <span className={`ph-tool-tag ${isReady ? 'ready' : 'soon'}`}>
                    {isReady ? t('common.ready') : t('common.soon')}
                  </span>
                </h3>
                <p>{stage.description}</p>
              </div>
              <span className="ph-tool-arrow">{i18n.dir() === 'rtl' ? '←' : '→'}</span>
            </div>
          );
        })}
      </div>

      {/* Story Bible Banner */}
      {hasStoryBible === false && (
        <div className="ph-sb-banner ph-sb-missing">
          <span className="ph-sb-text">
            <strong>{t('draftReview.storyBibleTitle')}</strong> {t('projects.storyBibleNotGenerated')}
          </span>
          <button
            className="ph-sb-btn"
            onClick={() =>
              navigate(`/projects/${safeProjectId}/draft-review?tab=overview`)
            }
          >
            {t('common.generate')}
          </button>
        </div>
      )}
      {hasStoryBible === true && (
        <div className="ph-sb-banner ph-sb-success">
          <div className="ph-sb-left">
            <span className="ph-sb-check">✓</span>
            <div>
              <div className="ph-sb-text">
                <strong>{t('draftReview.storyBibleTitle')}</strong> {t('projects.storyBibleGenerated')}
              </div>
            </div>
          </div>
          <button
            className="ph-sb-btn"
            onClick={() =>
              navigate(`/projects/${safeProjectId}/story-bible`)
            }
          >
            {t('projects.viewBible')}
          </button>
        </div>
      )}
      {showPdfImport && (
        <PdfImportModal
          projectId={safeProjectId}
          onClose={() => setShowPdfImport(false)}
          onSuccess={(msg) => {
            setImportMessage(msg);
            setTimeout(() => setImportMessage(''), 4000);
          }}
        />
      )}

      {importMessage && (
        <div className="ph-import-toast">{importMessage}</div>
      )}
    </div>
  );
}

export default ProjectHome;
